import * as THREE from 'three/webgpu';
import type {SerializablePlainData} from '../../../../history/domain/SerializablePlainData';
import type {SketchSolverIslandSnapshot} from '../../../../history/solver';
import type {ISketchObject, SketchPointObject} from '../../../../mesh/sketch';
import type {SketchSolverRuntimeGraph} from './SketchSolverObjectCollector';
import {collectSketchSolverRuntimeGraph} from './SketchSolverObjectCollector';
import {restoreMissingSketchRelationDefinitions} from './SketchRelationDefinitionRestorer';
import {refreshSlotOwnersDerivedVisuals} from '../../../slot/refreshSlotOwnersDerivedVisuals';

/**
 * 草图 solver 运行时恢复输入。
 */
export interface RestoreSketchSolverRuntimeSnapshotInput {
    /** 当前运行时 seed 对象。 */
    readonly seedObjects: readonly unknown[];
    /** 要恢复到的 solver island snapshot。 */
    readonly snapshot: SketchSolverIslandSnapshot;
}

/**
 * 读取对象约束容器。
 *
 * @param object 草图对象。
 * @returns 约束容器。
 */
function readSketchRelationContainer(object: unknown): unknown {
    if (!object || typeof object !== 'object') {
        return null;
    }

    return Reflect.get(object, 'constraints');
}

/**
 * 从单个对象上移除约束。
 *
 * @param object 草图对象。
 * @param runtimeId 约束运行时 ID。
 */
function removeConstraintFromObject(object: unknown, runtimeId: number): void {
    const constraints = readSketchRelationContainer(object);
    if (!constraints || typeof constraints !== 'object') {
        return;
    }

    const remover = Reflect.get(constraints, 'removeRelation');
    if (typeof remover !== 'function') {
        return;
    }

    remover.call(constraints, runtimeId);
}

/**
 * 移除当前运行时里 snapshot 不再允许存在的新增约束。
 *
 * 说明：
 * 该能力用于“历史提交失败时回滚 relation-added”。当前项目尚无统一 relation-deleted 命令，
 * 因此缺失的旧约束不在这里重建，后续删除关系事务应由正式 command 层提供 constructor replay。
 *
 * @param graph 当前运行时图。
 * @param snapshot 目标 snapshot。
 */
function removeUnexpectedConstraints(
    graph: SketchSolverRuntimeGraph,
    snapshot: SketchSolverIslandSnapshot,
): void {
    const allowedConstraintIds = new Set(snapshot.constraintIds);
    const owners: unknown[] = [
        ...graph.entityObjects.values(),
        ...graph.referenceObjects.values(),
        ...graph.pointObjects.values(),
    ];

    graph.constraintObjects.forEach((constraint, constraintId) => {
        if (allowedConstraintIds.has(constraintId)) {
            return;
        }

        owners.forEach((owner) => {
            removeConstraintFromObject(owner, constraint.id);
        });
    });
}

/**
 * 判断值是否为可写 plain record。
 *
 * @param value 待判断值。
 * @returns 是否为 plain record。
 */
function isPlainRecord(value: unknown): value is Record<string, SerializablePlainData> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

/**
 * 克隆可序列化数据，避免把 snapshot 引用直接挂回运行时对象。
 *
 * @param value 可序列化数据。
 * @returns 克隆数据。
 */
function cloneSerializableData(value: SerializablePlainData): SerializablePlainData {
    return JSON.parse(JSON.stringify(value)) as SerializablePlainData;
}

/**
 * 从可序列化数据中读取三维向量。
 *
 * @param value 可序列化数据。
 * @returns 三维向量；非法时返回 null。
 */
function readVector3(value: SerializablePlainData | undefined): THREE.Vector3 | null {
    if (!Array.isArray(value) || value.length < 3) {
        return null;
    }

    const [x, y, z] = value;
    if (typeof x !== 'number' || typeof y !== 'number' || typeof z !== 'number') {
        return null;
    }

    return new THREE.Vector3(x, y, z);
}

/**
 * 读取有限数。
 *
 * @param value 可序列化数据。
 * @returns 有限数；非法时返回 undefined。
 */
function readFiniteNumber(value: SerializablePlainData | undefined): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * 恢复运行时实体的 geometryData。
 *
 * @param entity 草图实体。
 * @param geometry edge snapshot 几何字段。
 */
function restoreEntityGeometryData(
    entity: ISketchObject,
    geometry: Readonly<Record<string, SerializablePlainData>>,
): void {
    const geometryData = geometry.geometryData;
    if (geometryData === undefined) {
        return;
    }

    Reflect.set(entity, 'geometryData', cloneSerializableData(geometryData));
}

/**
 * 恢复圆弧定义。
 *
 * @param entity 草图实体。
 * @param geometry edge snapshot 几何字段。
 */
function restoreArcDefinition(
    entity: ISketchObject,
    geometry: Readonly<Record<string, SerializablePlainData>>,
): void {
    const updateArcDefinition = Reflect.get(entity, 'updateArcDefinition');
    const arcDefinition = geometry.arcDefinition;
    if (typeof updateArcDefinition !== 'function' || !isPlainRecord(arcDefinition)) {
        return;
    }

    const centerWorld = readVector3(arcDefinition.centerWorld);
    const radius = readFiniteNumber(arcDefinition.radius);
    const startAngle = readFiniteNumber(arcDefinition.startAngle);
    const endAngle = readFiniteNumber(arcDefinition.endAngle);
    const clockwise = arcDefinition.clockwise;
    if (!centerWorld || radius === undefined || startAngle === undefined || endAngle === undefined) {
        return;
    }

    updateArcDefinition.call(entity, {
        centerWorld,
        radius,
        startAngle,
        endAngle,
        clockwise: typeof clockwise === 'boolean' ? clockwise : false,
        segments: readFiniteNumber(arcDefinition.segments),
    });
}

/**
 * 恢复椭圆定义。
 *
 * @param entity 草图实体。
 * @param geometry edge snapshot 几何字段。
 */
function restoreEllipseDefinition(
    entity: ISketchObject,
    geometry: Readonly<Record<string, SerializablePlainData>>,
): void {
    const updateEllipseDefinition = Reflect.get(entity, 'updateEllipseDefinition');
    const ellipseDefinition = geometry.ellipseDefinition;
    if (typeof updateEllipseDefinition !== 'function' || !isPlainRecord(ellipseDefinition)) {
        return;
    }

    const centerWorld = readVector3(ellipseDefinition.centerWorld);
    const radiusX = readFiniteNumber(ellipseDefinition.radiusX);
    const radiusY = readFiniteNumber(ellipseDefinition.radiusY);
    const rotation = readFiniteNumber(ellipseDefinition.rotation);
    const startAngle = readFiniteNumber(ellipseDefinition.startAngle);
    const endAngle = readFiniteNumber(ellipseDefinition.endAngle);
    const clockwise = ellipseDefinition.clockwise;
    if (
        !centerWorld
        || radiusX === undefined
        || radiusY === undefined
        || rotation === undefined
        || startAngle === undefined
        || endAngle === undefined
    ) {
        return;
    }

    updateEllipseDefinition.call(entity, {
        centerWorld,
        radiusX,
        radiusY,
        rotation,
        startAngle,
        endAngle,
        clockwise: typeof clockwise === 'boolean' ? clockwise : false,
        segments: readFiniteNumber(ellipseDefinition.segments),
    });
}

/**
 * 恢复实体基础几何属性。
 *
 * @param graph 当前运行时图。
 * @param snapshot 目标 snapshot。
 */
function restoreEntityGeometryStates(
    graph: SketchSolverRuntimeGraph,
    snapshot: SketchSolverIslandSnapshot,
): void {
    snapshot.edgeStates.forEach((edgeState) => {
        const entity = graph.entityObjects.get(edgeState.entityId);
        if (!entity) {
            throw new Error(`无法回滚 solver island，缺少实体 ${edgeState.entityId} 的运行时对象。`);
        }

        if (typeof edgeState.construction === 'boolean') {
            Reflect.set(entity, 'isConstruction', edgeState.construction);
        }

        const radius = readFiniteNumber(edgeState.geometry.radius);
        if (radius !== undefined && Reflect.has(entity, 'radius')) {
            Reflect.set(entity, 'radius', radius);
        }

        restoreEntityGeometryData(entity, edgeState.geometry);
        restoreArcDefinition(entity, edgeState.geometry);
        restoreEllipseDefinition(entity, edgeState.geometry);
    });
}

/**
 * 将 snapshot 点状态转换为世界坐标。
 *
 * @param point 当前运行时点。
 * @param pointState snapshot 点状态。
 * @returns 世界坐标。
 */
function resolvePointWorldPosition(
    point: SketchPointObject,
    pointState: SketchSolverIslandSnapshot['pointStates'][number],
): THREE.Vector3 {
    if (pointState.worldPosition) {
        return new THREE.Vector3(
            pointState.worldPosition[0],
            pointState.worldPosition[1],
            pointState.worldPosition[2],
        );
    }

    return point.referencePlane.localToWorld(new THREE.Vector2(
        pointState.localPosition[0],
        pointState.localPosition[1],
    ));
}

/**
 * 恢复点位置。
 *
 * @param graph 当前运行时图。
 * @param snapshot 目标 snapshot。
 */
function restorePointStates(
    graph: SketchSolverRuntimeGraph,
    snapshot: SketchSolverIslandSnapshot,
): void {
    snapshot.pointStates.forEach((pointState) => {
        const point = graph.pointObjects.get(pointState.pointId);
        if (!point) {
            throw new Error(`无法回滚 solver island，缺少点 ${pointState.pointId} 的运行时对象。`);
        }

        point.syncPositionSilently(resolvePointWorldPosition(point, pointState));
    });
}

/**
 * 刷新实体几何。
 *
 * @param entity 草图实体。
 */
function refreshEntityGeometry(entity: ISketchObject): void {
    const refreshGeometryOnly = Reflect.get(entity, 'refreshGeometryOnly');
    if (typeof refreshGeometryOnly === 'function') {
        refreshGeometryOnly.call(entity);
    }

    const refreshContourGeometry = Reflect.get(entity, 'refreshContourGeometry');
    if (typeof refreshContourGeometry === 'function') {
        refreshContourGeometry.call(entity);
    }
}

/**
 * 刷新当前运行时图内实体几何。
 *
 * @param graph 当前运行时图。
 */
function refreshRuntimeGeometry(graph: SketchSolverRuntimeGraph): void {
    graph.entityObjects.forEach((entity) => {
        refreshEntityGeometry(entity);
    });
}

/**
 * 恢复草图 solver island 运行时状态。
 *
 * 设计说明：
 * 1. 该函数只服务于历史提交失败时的紧急回滚，不替代正式 undo/redo PatchApplier；
 * 2. 回滚范围限于当前 Phase 2 已接入的草图拖拽与 relation-added；
 * 3. BRep、Mesh、渲染缓存等派生状态不进入 snapshot，只通过几何刷新重新派生。
 *
 * @param input 恢复输入。
 * @throws 当无法从种子对象收集当前 solver 运行时图时抛出错误。
 */
export function restoreSketchSolverRuntimeSnapshot(input: RestoreSketchSolverRuntimeSnapshotInput): void {
    const graph = collectSketchSolverRuntimeGraph({
        seedObjects: input.seedObjects,
    });

    if (!graph) {
        throw new Error(`无法回滚 solver island ${input.snapshot.islandId}，当前运行时图为空。`);
    }

    removeUnexpectedConstraints(graph, input.snapshot);
    restoreMissingSketchRelationDefinitions(graph, input.snapshot);
    restoreEntityGeometryStates(graph, input.snapshot);
    restorePointStates(graph, input.snapshot);
    refreshRuntimeGeometry(graph);
    /**
     * 槽口宿主的派生视觉（中心线辅助虚线 + 上色轮廓）随几何恢复统一刷新：
     * 与参数求解写回共用同一份实现——Undo/Redo 曾漏掉这步，
     * 恢复后屏幕残留恢复前弧度的中心线。
     */
    refreshSlotOwnersDerivedVisuals(graph.entityObjects.values());
}
